import { Assets, Sprite, type Texture } from 'pixi.js';
import type { ZoneObject } from '@bratva/content';
import { gridToScreen } from './iso.js';

/**
 * Спрайты из Blender. Рендер устроен так, что центр footprint при z=0
 * попадает ровно в центр картинки, поэтому здесь нет ни одной таблицы
 * смещений: anchor (0.5, 0.5) и позиция центра footprint.
 *
 * Если атласа нет (не прогнали пайплайн, не доехала статика) — сцена
 * молча рисует коробки, как раньше. Игра не должна падать из-за арта.
 */

interface ManifestEntry {
  name: string;
  file: string;
  w: number;
  d: number;
}

let textures: Record<string, Texture> = {};
let ready = false;

export async function loadSprites(): Promise<boolean> {
  try {
    const response = await fetch('sprites/manifest.json');
    if (!response.ok) return false;
    const manifest = (await response.json()) as { assets: ManifestEntry[] };

    const loaded = await Promise.all(
      manifest.assets.map(async (entry) => [entry.name, await Assets.load<Texture>(`sprites/${entry.file}`)] as const),
    );
    textures = Object.fromEntries(loaded);
    ready = true;
    return true;
  } catch (error) {
    console.warn('Спрайты не загрузились, рисуем коробки:', error);
    return false;
  }
}

export function spritesReady(): boolean {
  return ready;
}

/** Панельки различаются цветом торца — по нему и выбирается вариант. */
const PANELKA_BY_TINT: Record<string, string> = {
  '#8A8578': 'panelka',
  '#7E8A80': 'panelka_cold',
  '#6E7684': 'panelka_grey',
  '#78706A': 'panelka_brown',
};

/** Какой ассет соответствует объекту зоны. */
export function assetFor(obj: ZoneObject): string | null {
  switch (obj.kind) {
    case 'panelka':
      return PANELKA_BY_TINT[obj.tint ?? ''] ?? (obj.w >= 9 ? 'panelka_grey' : 'panelka');
    case 'garazhi':
      return obj.w >= 10 ? 'garazhi' : 'garazhi_small';
    case 'zabor':
      // Забор не тянем по горизонтали: растянулся бы и штакетник.
      return obj.w > obj.h ? `zabor_${obj.w}` : 'zabor_v10';
    default:
      return obj.kind;
  }
}

function make(name: string, gx: number, gy: number): Sprite | null {
  const texture = textures[name];
  if (!texture) return null;

  const sprite = new Sprite(texture);
  sprite.anchor.set(0.5, 0.5);
  const point = gridToScreen(gx, gy);
  sprite.position.set(point.x, point.y);
  return sprite;
}

/** Спрайт объекта: позиционируется по центру своего footprint. */
export function objectSprite(obj: ZoneObject): Sprite | null {
  const name = assetFor(obj);
  return name ? make(name, obj.x + obj.w / 2, obj.y + obj.h / 2) : null;
}

/** Спрайт для клетки 1×1: тачка, пацан, патруль. */
export function tileSprite(name: string, gx: number, gy: number): Sprite | null {
  return make(name, gx + 0.5, gy + 0.5);
}
